import { HouseholdWorkflowTracker_WorkflowTrackerFragment } from '@/modules/workflowTrackers/graphql/HouseholdWorkflowTrackersContext.generated';
import {
  WorkflowTrackerKind as Kind,
  WorkflowTrackerStatus as Status,
} from '@/types/schema';

type Tracker = Pick<
  HouseholdWorkflowTracker_WorkflowTrackerFragment,
  | 'id'
  | 'householdID'
  | 'kind'
  | 'status'
  | 'createdAt'
  | 'startedAt'
  | 'presentation'
  | 'conversation'
>;

export type HouseholdSetupPhase =
  /** At least one initial snapshot has not arrived. Do not choose a setup screen yet. */
  | 'initializing'
  /** A required request failed. Show the normal overview, not inferred progress. */
  | 'unavailable'
  /** No active setup work or known setup completion. Show the normal overview. */
  | 'idle'
  /** The KG snapshot says document extraction is running. */
  | 'extracting'
  /** Active validation has a conversation and no answers submitted for this run. */
  | 'questions'
  /** Profile, presentation, or onboarding completion work is queued or running. */
  | 'processing'
  /** The onboarding completion workflow succeeded and no work or failures remain. */
  | 'complete'
  /** No work is active, but a relevant setup workflow failed. */
  | 'failed';

const profileKinds = new Set([
  Kind.DocumentExtraction,
  Kind.AiSuggestionsV2DpGeneration,
  Kind.AiSuggestionsV2EntityDetailsGeneration,
  Kind.AiSuggestionsV2HoldingsGeneration,
  Kind.AiSuggestionsV2HouseholdRegen,
  Kind.AiSuggestionsV2OnboardingGeneration,
  Kind.AiSuggestionsV2SubtrustDetailsGeneration,
]);
const presentationKinds = new Set([
  Kind.PresentationGeneration,
  Kind.PresentationTemplatePromptGeneration,
  Kind.EstateSnapshotGeneration,
]);
const isActiveTracker = (tracker: Tracker) =>
  tracker.status === Status.Started || tracker.status === Status.Queued;
const isProcessingTracker = (tracker: Tracker) =>
  profileKinds.has(tracker.kind) ||
  presentationKinds.has(tracker.kind) ||
  tracker.kind === Kind.HouseholdOnboardingCompletion;

function getValidationQuestionState(
  validationQuestionsTracker: Tracker | undefined,
  validationQuestionsAnsweredAt: Date | null,
  acceptedConversationId: string | undefined
) {
  // This household-wide timestamp is not reset when questions are regenerated.
  const submitted =
    (!!acceptedConversationId &&
      acceptedConversationId ===
        validationQuestionsTracker?.conversation?.id) ||
    (!!validationQuestionsAnsweredAt &&
      !!validationQuestionsTracker &&
      validationQuestionsAnsweredAt >= validationQuestionsTracker.createdAt);
  const validationRunning =
    !!validationQuestionsTracker && isActiveTracker(validationQuestionsTracker);
  return {
    submitted,
    validationRunning,
    conversationId:
      validationRunning && !submitted
        ? validationQuestionsTracker.conversation?.id
        : undefined,
  };
}

function getSetupWorkState(
  householdTrackers: readonly Tracker[],
  onboardingCompletionTracker: Tracker | undefined,
  validationQuestionsTracker: Tracker | undefined,
  validationRunning: boolean
) {
  const profileWorkflowTrackers = householdTrackers.filter((tracker) =>
    profileKinds.has(tracker.kind)
  );
  const presentationWorkflowTrackers = householdTrackers.filter((tracker) =>
    presentationKinds.has(tracker.kind)
  );
  const onboardingRunning =
    !!onboardingCompletionTracker &&
    isActiveTracker(onboardingCompletionTracker);
  // completeHouseholdOnboarding creates this tracker when the wizard finishes,
  // not on later uploads. Validation can finish before that request starts.
  // Only a missing tracker means we must wait for validation to start.
  const awaitingValidationStart =
    onboardingRunning && !validationQuestionsTracker;

  // Creation times guide failure/presentation display; they do not prove that
  // these workflows belong to the same request. Active work uses all trackers.
  const trackersCreatedSinceOnboardingRequest = householdTrackers.filter(
    (tracker) =>
      !onboardingCompletionTracker ||
      tracker.createdAt >= onboardingCompletionTracker.createdAt
  );
  // Snapshot and template workflows create presentations. PRESENTATION_GENERATION
  // exports a PDF and must not replace the latest content-generation result.
  const latestPresentationGeneration = householdTrackers.find(
    (tracker) =>
      tracker.kind === Kind.EstateSnapshotGeneration ||
      tracker.kind === Kind.PresentationTemplatePromptGeneration
  );
  const setupPresentationTrackers = presentationWorkflowTrackers.filter(
    (tracker) =>
      isActiveTracker(tracker) ||
      (trackersCreatedSinceOnboardingRequest.includes(tracker) &&
        tracker.status === Status.Completed &&
        !!tracker.presentation?.id)
  );
  // Prompt and generation workflows can refer to the same presentation.
  const presentationCount = new Set(
    setupPresentationTrackers.map(
      (tracker) => tracker.presentation?.id ?? tracker.id
    )
  ).size;
  return {
    profileRunning:
      validationRunning ||
      profileWorkflowTrackers.some(isActiveTracker) ||
      awaitingValidationStart,
    presentationRunning: presentationWorkflowTrackers.some(isActiveTracker),
    presentationCount,
    singlePresentationId:
      presentationCount === 1
        ? setupPresentationTrackers[0]?.presentation?.id
        : undefined,
    onboardingRunning,
    awaitingValidationStart,
    onboardingFailed: onboardingCompletionTracker?.status === Status.Failed,
    presentationFailed: latestPresentationGeneration?.status === Status.Failed,
    hasPresentation: trackersCreatedSinceOnboardingRequest.some(
      (tracker) =>
        presentationKinds.has(tracker.kind) &&
        tracker.status === Status.Completed &&
        !!tracker.presentation?.id
    ),
  };
}

function getOverviewRefreshTrackerIds(householdTrackers: readonly Tracker[]) {
  // Refresh after workflows can write household data, even on partial failure.
  // Extraction alone does not mean those writes have happened.
  return householdTrackers
    .filter((tracker) => {
      const writesOverviewData =
        (profileKinds.has(tracker.kind) &&
          tracker.kind !== Kind.DocumentExtraction) ||
        presentationKinds.has(tracker.kind) ||
        tracker.kind === Kind.ValidationQuestions ||
        tracker.kind === Kind.HouseholdOnboardingCompletion;
      const finished =
        tracker.status === Status.Completed || tracker.status === Status.Failed;
      return writesOverviewData && finished;
    })
    .map((tracker) => tracker.id);
}

/** Derive setup progress from extraction, validation questions, and workflow trackers. */
export function householdSetupPhase({
  householdId,
  trackers,
  ready,
  unavailable,
  extracting,
  validationQuestionsAnsweredAt,
  acceptedConversationId,
  initialSetupTrackerId,
  documentExtractionHistoryIds = [],
}: {
  householdId: string;
  trackers: readonly Tracker[];
  /** All initial tracker, KG, and answer-status snapshots have arrived. */
  ready: boolean;
  /** A required snapshot request failed and has not recovered. */
  unavailable: boolean;
  /** The KG snapshot reports active document extraction. */
  extracting: boolean;
  validationQuestionsAnsweredAt: Date | null;
  /** Current conversation's response is durable; its household timestamp may lag. */
  acceptedConversationId?: string;
  /** Initial-create provenance stored by this browser, scoped to one workflow. */
  initialSetupTrackerId?: string;
  /** Latest two extraction runs, queried independently of the recent activity page. */
  documentExtractionHistoryIds?: readonly string[];
}) {
  const householdTrackers = trackers
    .filter((tracker) => tracker.householdID === householdId)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const onboardingCompletionTracker = householdTrackers.find(
    (tracker) => tracker.kind === Kind.HouseholdOnboardingCompletion
  );
  const validationTracker = householdTrackers.find(
    (tracker) => tracker.kind === Kind.ValidationQuestions
  );
  const questions = getValidationQuestionState(
    validationTracker,
    validationQuestionsAnsweredAt,
    acceptedConversationId
  );
  const { awaitingValidationStart, ...work } = getSetupWorkState(
    householdTrackers,
    onboardingCompletionTracker,
    validationTracker,
    questions.validationRunning
  );
  const activeProcessingTracker = householdTrackers.find(
    (tracker) => isActiveTracker(tracker) && isProcessingTracker(tracker)
  );
  const activeOnboardingTracker =
    onboardingCompletionTracker && isActiveTracker(onboardingCompletionTracker)
      ? onboardingCompletionTracker
      : undefined;

  let phase: HouseholdSetupPhase = 'idle';
  if (unavailable) phase = 'unavailable';
  else if (!ready) phase = 'initializing';
  else if (extracting) phase = 'extracting';
  else if (questions.conversationId) phase = 'questions';
  // Keep showing active work when another workflow has failed.
  else if (
    work.profileRunning ||
    work.presentationRunning ||
    work.onboardingRunning
  )
    phase = 'processing';
  else if (work.presentationFailed || work.onboardingFailed) phase = 'failed';
  // Only this workflow proves that household processing and requested
  // presentations finished. Extraction completion does not.
  else if (onboardingCompletionTracker?.status === Status.Completed)
    phase = 'complete';

  const isInitialRun =
    !!initialSetupTrackerId &&
    initialSetupTrackerId === onboardingCompletionTracker?.id &&
    work.onboardingRunning;
  // A previous answer or completed validation ends initial focus, including
  // validation that finished without asking any questions.
  const validationFinished =
    !!validationQuestionsAnsweredAt ||
    questions.submitted ||
    validationTracker?.status === Status.Completed;

  // Answer completion is independent of the remaining onboarding work.
  let validationQuestionsState: 'completed' | 'preparing' | 'unavailable';
  if (validationQuestionsAnsweredAt || acceptedConversationId) {
    validationQuestionsState = 'completed';
  } else if (unavailable) {
    validationQuestionsState = 'unavailable';
  } else if (!ready) {
    validationQuestionsState = 'preparing';
  } else if (extracting) {
    validationQuestionsState = 'preparing';
  } else if (awaitingValidationStart || questions.validationRunning) {
    validationQuestionsState = 'preparing';
  } else {
    validationQuestionsState = 'unavailable';
  }

  // Onboarding uses the saved validation milestone, not child workflow starts.
  let processingStartedAt = activeOnboardingTracker
    ? validationQuestionsAnsweredAt
    : activeProcessingTracker?.startedAt;
  // Extraction trackers can remain active after onboarding finishes. Exclude
  // their validation wait time, but keep later standalone job start times.
  if (
    processingStartedAt &&
    validationQuestionsAnsweredAt &&
    processingStartedAt < validationQuestionsAnsweredAt
  ) {
    processingStartedAt = validationQuestionsAnsweredAt;
  }

  return {
    phase,
    validationQuestionsState,
    isInitialSetup: isInitialRun && !validationFinished,
    // One tracker covers an upload batch, not each document. Include live
    // arrivals so a second upload changes the heading without a page reload.
    isFirstDocumentUpload:
      new Set([
        ...documentExtractionHistoryIds,
        ...householdTrackers
          .filter((tracker) => tracker.kind === Kind.DocumentExtraction)
          .map((tracker) => tracker.id),
      ]).size <= 1,
    onboardingCompletionTrackerId: onboardingCompletionTracker?.id,
    conversationId: questions.conversationId,
    submitted: questions.submitted,
    ...work,
    preparingQuestions:
      !validationQuestionsAnsweredAt &&
      !questions.submitted &&
      !questions.conversationId &&
      (awaitingValidationStart || questions.validationRunning),
    processingStartedAt,
    overviewRefreshTrackerIds: getOverviewRefreshTrackerIds(householdTrackers),
  };
}
